import { EMU, initEnvironment } from '@/globals/typescript/utils/host'
import {
  initializeApp,
  type FirebaseApp,
  type FirebaseOptions,
} from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import {
  connectFirestoreEmulator,
  getFirestore,
  type Firestore,
} from 'firebase/firestore'

import {
  connectStorageEmulator,
  getStorage,
  type FirebaseStorage,
} from 'firebase/storage'

// Optional imports – uncomment as needed
// import { getFunctions } from 'firebase/functions'

/**
 * Firebase services
 * Assigned by initFirebase() — never read these at module scope.
 */
export let app: FirebaseApp
export let auth: Auth
export let db: Firestore
export let storage: FirebaseStorage

/**
 * Initialize Firebase
 * Fetches the Firebase SDK config from Firebase Hosting's reserved
 * `/__/firebase/init.json` endpoint and derives all environment-specific
 * values from the returned `projectId`. Must be awaited before any
 * Firebase service is used.
 */
export async function initFirebase(): Promise<void> {
  const response = await fetch('/__/firebase/init.json')
  if (!response.ok) {
    throw new Error(
      `Failed to fetch Firebase config from /__/firebase/init.json (${response.status} ${response.statusText})`
    )
  }
  const firebaseConfig = (await response.json()) as FirebaseOptions & {
    projectId: string
  }

  initEnvironment(firebaseConfig.projectId)

  app = initializeApp(firebaseConfig)
  auth = getAuth(app)
  db = getFirestore(app)

  if (EMU.isEmulatingFirestore) {
    connectFirestoreEmulator(db, 'localhost', 8080)
  }

  storage = getStorage(app)
  if (EMU.isEmulatingStorage) {
    connectStorageEmulator(storage, 'localhost', 9199)
  }

  // Optional services
  // const functions = getFunctions(app)
}
